import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ModeToggle({ mode, autoConsent, killSwitch, onChange }) {
  const [pendingConsent, setPendingConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const flipMode = async (newMode) => {
    if (killSwitch) {
      toast.error("Release the kill switch before changing mode.");
      return;
    }
    if (newMode === "auto" && !autoConsent) {
      setPendingConsent(true);
      return;
    }
    setBusy(true);
    try {
      const r = await api.put("/risk/settings", { execution_mode: newMode });
      onChange?.(r.data);
      toast.success(`Execution mode → ${newMode.toUpperCase()}`);
    } catch (e) {
      toast.error("Failed to update mode.");
    } finally {
      setBusy(false);
    }
  };

  const acceptConsent = async () => {
    setBusy(true);
    try {
      const r = await api.put("/risk/settings", {
        execution_mode: "auto",
        auto_consent_acknowledged: true,
      });
      onChange?.(r.data);
      toast.success("Auto mode enabled. AI will execute approved signals automatically.");
      setPendingConsent(false);
    } catch (e) {
      toast.error("Failed to enable auto mode.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="mode-toggle">
        <button
          type="button"
          className="mode-pill"
          data-mode={mode}
          data-testid={`mode-current-${mode}`}
          onClick={() => flipMode(mode === "auto" ? "manual" : "auto")}
          disabled={busy}
          title="Click to toggle execution mode"
        >
          <span className="dot" />
          <span>{mode === "auto" ? "AUTO" : "MANUAL"}</span>
        </button>
      </div>

      {pendingConsent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.85)" }} data-testid="auto-consent-modal">
          <div className="panel w-[480px] p-6">
            <h2 className="text-lg font-semibold tracking-tight mb-2">Enable Autonomous Trading</h2>
            <p className="text-sm text-[#8B95A5] leading-relaxed">
              In <span className="text-[#00E5FF]">AUTO</span> mode the AI agent will place orders on your
              connected brokers <span className="text-white">without</span> manual approval.
            </p>
            <ul className="text-xs text-[#8B95A5] mt-3 space-y-2 list-disc list-inside">
              <li>Only signals with confidence ≥ 0.65 will execute.</li>
              <li>All trades pass through your risk guardrails.</li>
              <li>The kill switch instantly reverts to manual mode.</li>
              <li>You can switch back to manual at any time.</li>
            </ul>
            <div className="text-xs text-[#FFB300] mt-3 font-mono">
              I understand and explicitly consent to autonomous order placement.
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button
                className="btn-outline"
                onClick={() => setPendingConsent(false)}
                disabled={busy}
                data-testid="auto-consent-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={acceptConsent}
                disabled={busy}
                data-testid="auto-consent-accept-btn"
              >
                {busy ? "Enabling..." : "I consent — Enable AUTO"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
